import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Modal } from '@televet/kibble-ui/Modal';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Code, Flex, Table } from '@televet/kibble-ui/chakra';
import { useCallback, useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { PageContainer } from '@/components/PageContainer';
import { TablePagination } from '@/components/TablePagination';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import { ExecutionResultFilters } from './-components/ExecutionResultFilters';
import { ExecutionChainLinks } from './-components/ExecutionChainLinks';
import {
  executionResultsSearchSchema,
  yesNoToBoolean,
  type ExecutionResultsFilterValues,
} from './-lib/resultFilters';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyableId } from '@/components/CopyableId';
import { LinkText } from '@/components/LinkText';
import { CodeEditor } from '@/components/CodeEditor';
import { toaster } from '@/components/ui/toaster';
import {
  EXECUTION_MODE_CONFIG,
  formatDuration,
  namespacePalette,
} from '../-lib/executionConstants';
import { EXECUTION_MODE_TAG_VARIANT } from './-lib/records';
import { ExecutionStatusTag } from '../-components/StatusTag';

export const Route = createFileRoute('/_authenticated/emitters/executions/$executionId')({
  component: ExecutionDetail,
  validateSearch: executionResultsSearchSchema,
});

/** Deliberately small: a heavy execution should not render thousands of rows. */
const PAGE_SIZE = 25;

function ExecutionDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { executionId } = Route.useParams();

  const {
    data: execution,
    isLoading,
    isError,
    error: loadError,
  } = useQuery(trpc.emitter.getExecution.queryOptions({ id: executionId }));

  const { page = 1, ...filters } = Route.useSearch();

  const applyFilters = useCallback(
    (next: ExecutionResultsFilterValues) => {
      // Any filter change invalidates the current page number.
      navigate({ to: '.', search: { ...next, page: undefined } });
    },
    [navigate]
  );

  const [entityIdDraft, setEntityIdDraft] = useDebouncedFilterText({
    value: filters.entityId,
    onCommit: (next) =>
      navigate({
        to: '.',
        replace: true,
        search: (prev) => ({ ...prev, entityId: next, page: undefined }),
      }),
  });

  const { data: resultsPage, isFetching: isLoadingResults } = useQuery({
    ...trpc.emitter.getExecutionResults.queryOptions({
      executionId,
      page,
      limit: PAGE_SIZE,
      ...(filters.entityId ? { entityId: filters.entityId } : {}),
      ...(filters.status?.length ? { status: filters.status } : {}),
      ...(yesNoToBoolean(filters.emitted) !== undefined
        ? { wasEmitted: yesNoToBoolean(filters.emitted) }
        : {}),
      ...(yesNoToBoolean(filters.delivered) !== undefined
        ? { eventDelivered: yesNoToBoolean(filters.delivered) }
        : {}),
    }),
    enabled: !!execution,
  });

  // Blaming the filters for an empty table is wrong when none are set, and an
  // execution that produced nothing is the common case.
  const hasActiveFilters = Boolean(
    filters.entityId ||
    filters.status?.length ||
    filters.emitted?.length ||
    filters.delivered?.length
  );

  const goToPage = useCallback(
    (nextPage: number) => {
      // Omit page 1 from the URL to keep the default view clean.
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, page: nextPage > 1 ? nextPage : undefined }),
      });
    },
    [navigate]
  );

  // Retry mutation
  const { mutate: retryExecution, isPending: isRetrying } = useMutation(
    trpc.emitter.retryExecution.mutationOptions({
      onSuccess: (newExec) => {
        toaster.success({ title: 'Retry queued -- new execution created' });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'getExecution']],
        });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listExecutions']],
        });
        navigate({ to: `/emitters/executions/${newExec.id}` });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to retry execution',
          description: error.message,
        });
      },
    })
  );

  // Selected result for message payload preview
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);

  const selectedResult = useMemo(() => {
    if (!selectedResultId || !resultsPage) return null;
    return resultsPage.items.find((r) => r.id === selectedResultId) ?? null;
  }, [selectedResultId, resultsPage]);

  // Reconstruct the EmitterEvent message payload for a given result
  const messagePayload = useMemo(() => {
    if (!selectedResult || !execution) return null;

    const tv = execution.templateVersion;
    const inst = execution.instance;

    // Build field mapping
    const fieldMap: Record<string, string> = {};
    const rawMapping = (tv.eventFieldMapping ?? {}) as Record<string, unknown>;
    for (const [k, v] of Object.entries(rawMapping)) {
      if (typeof v === 'string') fieldMap[k] = v;
    }

    // Apply field mapping
    const promotableKeys = new Set(['entityType', 'entityId', 'ownerType', 'ownerId']);
    const promoted: Record<string, string | null> = {};
    const data: Record<string, unknown> = {};
    const rowData = (selectedResult.data ?? {}) as Record<string, unknown>;

    for (const [key, value] of Object.entries(rowData)) {
      const mappedKey = fieldMap[key] ?? key;
      if (promotableKeys.has(mappedKey)) {
        promoted[mappedKey] = value != null ? String(value) : null;
      } else {
        data[mappedKey] = value;
      }
    }

    // Static properties from template version
    const templateStatic = (tv.eventStaticProperties ?? {}) as Record<string, unknown>;
    const mergedProps = { ...templateStatic };

    return {
      eventName: tv.eventName ?? 'emitter-result.created',
      entityType: promoted.entityType ?? selectedResult.entityType,
      entityId: promoted.entityId ?? selectedResult.entityId,
      ownerType: promoted.ownerType ?? inst.ownerType,
      ownerId: promoted.ownerId ?? inst.ownerId,
      properties: mergedProps,
      data,
      context: {
        emitterResultId: selectedResult.id,
        emitterExecutionId: executionId,
        templateName: inst.template.name,
        instanceName: inst.name,
        executionScheduledAt: execution.scheduledAt
          ? new Date(execution.scheduledAt).toISOString()
          : null,
      },
    };
  }, [selectedResult, execution, executionId]);

  // Routing info derived from template version
  const routingInfo = useMemo(() => {
    if (!execution) return null;
    const tv = execution.templateVersion;
    return {
      type: 'SQS' as const,
      queue: tv.sqsQueue ?? 'Unknown',
      routingKey: tv.routingKey ?? 'Unknown',
    };
  }, [execution]);

  if (isLoading) {
    return (
      <PageContainer>
        <Flex p={8}>
          <Text variant="subtle">Loading execution...</Text>
        </Flex>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <Flex p={8}>
          <Text variant="danger">
            Failed to load execution: {loadError?.message ?? 'Unknown error'}
          </Text>
        </Flex>
      </PageContainer>
    );
  }

  if (!execution) {
    return (
      <PageContainer>
        <Flex p={8}>
          <Text variant="subtle">Execution not found</Text>
        </Flex>
      </PageContainer>
    );
  }

  const modeConfig = EXECUTION_MODE_CONFIG[execution.executionMode] ?? EXECUTION_MODE_CONFIG.DryRun;

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        {/* Header */}
        <Breadcrumbs
          items={[
            { label: 'Emitters', to: '/emitters' },
            { label: 'Executions', to: '/emitters/executions' },
            // Full id, unprefixed: the crumb is where you copy the id from, and
            // a truncated one cannot be used for that.
            { label: executionId },
          ]}
        />

        <Flex align="center" gap={3} wrap="wrap">
          <Text fontSize="2xl" fontWeight="bold">
            Execution Detail
          </Text>
          <ExecutionStatusTag status={execution.status} size="lg" />
          <Tag
            label={modeConfig.label}
            variant={EXECUTION_MODE_TAG_VARIANT[execution.executionMode] ?? 'success'}
            size="lg"
          />
          {execution.status === 'Failed' && (
            <Button
              size="sm"
              variant="secondary"
              iconName="arrowCircle"
              onClick={() => retryExecution({ id: execution.id })}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          )}
        </Flex>

        {/* Metadata cards: three across, or each on its own full-width row.
            Never 2 + 1.

            `wrap` is off in row mode on purpose. With it on, the moment the
            three stopped fitting the third dropped to a line of its own under
            the other two, which is the layout this is avoiding. Their combined
            min width is ~832px and the xl breakpoint leaves ~1044px of content
            width, so they fit whenever row mode applies. */}
        <Flex
          gap={4}
          direction={{ base: 'column', xl: 'row' }}
          wrap={{ base: 'wrap', xl: 'nowrap' }}
        >
          <Card flex={1} minW="280px">
            <Flex direction="column" gap={2}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                EXECUTION
              </Text>
              <CopyableId id={execution.id} label="execution" data-testid="execution-id" />
              <Flex gap={4} wrap="wrap">
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Template
                  </Text>
                  <Link
                    to="/emitter-templates/$templateId"
                    params={{ templateId: execution.instance.template.id }}
                    style={{ textDecoration: 'none' }}
                  >
                    <LinkText fontSize="sm" fontWeight="medium">
                      {execution.instance.template.name}
                    </LinkText>
                  </Link>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Version
                  </Text>
                  <Text fontSize="sm">v{execution.templateVersion.versionNumber}</Text>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Instance
                  </Text>
                  <Link
                    to="/emitters/$instanceId"
                    params={{ instanceId: execution.instance.id }}
                    style={{ textDecoration: 'none' }}
                  >
                    <LinkText fontSize="sm">
                      {execution.instance.name ?? execution.instance.id}
                    </LinkText>
                  </Link>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Namespace
                  </Text>
                  {execution.dataSourceNamespace ? (
                    <Tag
                      label={execution.dataSourceNamespace}
                      size="sm"
                      // The parent is a column Flex, which stretches its
                      // children, so without this the tag spans the whole
                      // field. The listing avoids it by sitting in a table
                      // cell.
                      alignSelf="flex-start"
                      variant={namespacePalette(execution.dataSourceNamespace)}
                    />
                  ) : (
                    <Text fontSize="sm">—</Text>
                  )}
                </Flex>
              </Flex>
              <Flex gap={4} wrap="wrap">
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Owner
                  </Text>
                  <Text fontSize="sm">
                    {execution.instance.ownerType}:{' '}
                    {execution.instance.ownerName ?? execution.instance.ownerId}
                  </Text>
                </Flex>
              </Flex>
              {execution.chainRootId && (
                <ExecutionChainLinks
                  executionId={execution.id}
                  chainRootId={execution.chainRootId}
                  previousExecutionId={execution.previousExecutionId}
                  retryOfId={execution.retryOfId}
                />
              )}
            </Flex>
          </Card>

          {/* Timing card */}
          <Card minW="280px">
            <Flex direction="column" gap={2}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                TIMING
              </Text>
              {/* Two per row. The children had no flex basis, so four full
                    timestamps sat on one line and the card stayed as wide as
                    their combined width however narrow the viewport got. */}
              <Flex gap={4} wrap="wrap">
                {execution.scheduledAt && (
                  <Flex direction="column" flex="1 1 40%" minW="140px">
                    <Text fontSize="xs" variant="subtle">
                      Scheduled At
                    </Text>
                    <Text fontSize="sm">{new Date(execution.scheduledAt).toLocaleString()}</Text>
                  </Flex>
                )}
                {execution.startedAt && (
                  <Flex direction="column" flex="1 1 40%" minW="140px">
                    <Text fontSize="xs" variant="subtle">
                      Started At
                    </Text>
                    <Text fontSize="sm">{new Date(execution.startedAt).toLocaleString()}</Text>
                  </Flex>
                )}
                {execution.completedAt && (
                  <Flex direction="column" flex="1 1 40%" minW="140px">
                    <Text fontSize="xs" variant="subtle">
                      Completed At
                    </Text>
                    <Text fontSize="sm">{new Date(execution.completedAt).toLocaleString()}</Text>
                  </Flex>
                )}
                {execution.durationMs != null && (
                  <Flex direction="column" flex="1 1 40%" minW="140px">
                    <Text fontSize="xs" variant="subtle">
                      Duration
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {formatDuration(execution.durationMs)}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Card>

          {/* Routing info card */}
          {routingInfo && (
            <Card minW="240px">
              <Flex direction="column" gap={2}>
                <Text fontSize="xs" variant="subtle" fontWeight="medium">
                  EVENT ROUTING
                </Text>
                <Tag label={routingInfo.type} variant="info" alignSelf="flex-start" />
                {/* Label above value, matching the other cards on this
                      page. Side by side, the fixed 70px label column clipped
                      "Routing Key" onto two lines. */}
                <Flex direction="column" gap={2}>
                  <Flex direction="column" align="flex-start">
                    <Text fontSize="xs" variant="subtle">
                      Queue
                    </Text>
                    <Code fontSize="xs">{routingInfo.queue}</Code>
                  </Flex>
                  <Flex direction="column" align="flex-start">
                    <Text fontSize="xs" variant="subtle">
                      Routing Key
                    </Text>
                    <Code fontSize="xs">{routingInfo.routingKey}</Code>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          )}
        </Flex>

        {/* Results metrics beside the parameters snapshot: the metrics need
            little width, and pairing them keeps the results table higher up the
            page. Wraps to two rows when the viewport is narrow. */}
        <Flex gap={4} wrap="wrap" align="stretch">
          {execution.totalResults != null && (
            // Wide enough for two or three metrics, and it never shrinks: the
            // snapshot beside it takes whatever width is left.
            <Card flexShrink={0} minW="260px">
              <Flex direction="column" gap={2}>
                <Text fontSize="xs" variant="subtle" fontWeight="medium">
                  RESULTS
                </Text>
                <Flex gap={6} wrap="wrap">
                  <Flex direction="column" align="center">
                    <Text fontSize="2xl" fontWeight="bold">
                      {execution.totalResults}
                    </Text>
                    <Text fontSize="xs" variant="subtle">
                      Total
                    </Text>
                  </Flex>
                  <Flex direction="column" align="center">
                    <Text fontSize="2xl" fontWeight="bold" variant="success">
                      {execution.newResults ?? 0}
                    </Text>
                    <Text fontSize="xs" variant="subtle">
                      New
                    </Text>
                  </Flex>
                  {execution.dedupedResults != null && execution.dedupedResults > 0 && (
                    <Flex direction="column" align="center">
                      <Text fontSize="2xl" fontWeight="bold" variant="warning">
                        {execution.dedupedResults}
                      </Text>
                      <Text fontSize="xs" variant="subtle">
                        Deduped
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Card>
          )}

          {/* Parameters snapshot */}
          {execution.parametersSnapshot &&
            Object.keys(execution.parametersSnapshot as Record<string, unknown>).length > 0 && (
              <Card flex="1" minW="320px">
                <Flex direction="column" gap={2}>
                  <Text fontSize="xs" variant="subtle" fontWeight="medium">
                    PARAMETERS SNAPSHOT
                  </Text>
                  <CodeEditor
                    value={JSON.stringify(execution.parametersSnapshot, null, 2)}
                    onChange={() => {}}
                    language="json"
                    readOnly
                    minHeight="120px"
                  />
                </Flex>
              </Card>
            )}
        </Flex>

        {/* Error details for failed executions */}
        {execution.status === 'Failed' && execution.errorMessage && (
          <Card invalid>
            <Flex direction="column" gap={2}>
              <Text fontSize="xs" variant="danger" fontWeight="medium" textTransform="uppercase">
                Error Details
              </Text>
              <Text fontSize="sm" variant="danger" fontWeight="medium">
                {execution.errorMessage}
              </Text>
              {execution.errorStack && (
                <Code
                  fontSize="xs"
                  p={3}
                  display="block"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                  bg="background.danger"
                  color="text.danger"
                  borderRadius="md"
                  maxH="300px"
                  overflow="auto"
                >
                  {execution.errorStack}
                </Code>
              )}
            </Flex>
          </Card>
        )}

        {/* Results table */}
        <Flex direction="column" gap={3}>
          <Text fontSize="lg" fontWeight="semibold">
            Results
          </Text>

          <ExecutionResultFilters
            values={filters}
            onChange={applyFilters}
            entityIdDraft={entityIdDraft}
            onEntityIdDraftChange={setEntityIdDraft}
          />

          <Table.Root size="sm" width="100%">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Entity Type</Table.ColumnHeader>
                <Table.ColumnHeader>Entity ID</Table.ColumnHeader>
                <Table.ColumnHeader>Identity Hash</Table.ColumnHeader>
                <Table.ColumnHeader>Emitted</Table.ColumnHeader>
                <Table.ColumnHeader>Delivered</Table.ColumnHeader>
                <Table.ColumnHeader>Created</Table.ColumnHeader>
                <Table.ColumnHeader>Run</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">Payload</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {resultsPage?.items.map((result) => (
                <Table.Row key={result.id}>
                  <Table.Cell>
                    <Tag label={result.entityType ?? '-'} variant="default" size="sm" />
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" fontFamily="mono" maxW="200px" truncated>
                      {result.entityId ?? '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      fontSize="xs"
                      fontFamily="mono"
                      maxW="120px"
                      truncated
                      title={result.identityHash ?? undefined}
                    >
                      {result.identityHash ? result.identityHash.slice(0, 12) + '...' : '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Tag
                      label={result.wasEmitted ? 'Yes' : 'No'}
                      variant={result.wasEmitted ? 'success' : 'default'}
                      size="sm"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Tag
                      label={result.eventDelivered ? 'Yes' : 'No'}
                      variant={result.eventDelivered ? 'success' : 'default'}
                      size="sm"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" variant="subtle">
                      {new Date(result.createdAt).toLocaleString()}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {/* Absent for a result whose event was never picked up, so
                        there is genuinely no run to point at. */}
                    {resultsPage?.runIdByResultId[result.id] ? (
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() =>
                          navigate({
                            to: `/workflow-runs/${resultsPage.runIdByResultId[result.id]}`,
                          })
                        }
                        data-testid={`execution-result-run-link-${result.id}`}
                      >
                        View
                      </Button>
                    ) : (
                      <Text fontSize="xs" variant="subtle">
                        —
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <Button
                      size="xs"
                      variant="secondary"
                      onClick={() => setSelectedResultId(result.id)}
                    >
                      View
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          {isLoadingResults && (
            <Text variant="subtle" fontSize="sm" p={2}>
              Loading results...
            </Text>
          )}

          {!isLoadingResults && resultsPage?.items.length === 0 && (
            <Flex justify="center" p={4}>
              <Text variant="subtle">
                {hasActiveFilters
                  ? 'No results match these filters'
                  : 'No results for this execution'}
              </Text>
            </Flex>
          )}

          {resultsPage && resultsPage.pagination.total > 0 && (
            <TablePagination
              page={resultsPage.pagination.page}
              totalPages={resultsPage.pagination.totalPages}
              hasPrev={resultsPage.pagination.hasPrev}
              hasNext={resultsPage.pagination.hasNext}
              onPageChange={goToPage}
              summary={`${resultsPage.items.length} of ${resultsPage.pagination.total} results`}
              data-testid-prefix="execution-results"
            />
          )}
        </Flex>

        {/* Message payload modal */}
        <Modal
          lazyMount
          open={!!selectedResultId}
          onOpenChange={(e) => {
            if (!e.open) setSelectedResultId(null);
          }}
          size="lg"
          header={
            <Flex align="center" gap={2}>
              <Text>Message Payload</Text>
              <Tag label={`SQS -> ${routingInfo?.routingKey}`} variant="info" />
            </Flex>
          }
          hideFooter
          contentProps={{ maxW: '720px' }}
          closeButtonProps={{
            size: 'sm',
            'aria-label': 'Close',
            'data-testid': 'execution-payload-dialog-close',
          }}
        >
          {selectedResult && (
            <Flex direction="column" gap={2}>
              <Flex gap={3} wrap="wrap">
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Result ID
                  </Text>
                  <Text fontSize="xs" fontFamily="mono">
                    {selectedResult.id}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Entity
                  </Text>
                  <Text fontSize="xs">
                    {selectedResult.entityType ?? '-'} / {selectedResult.entityId ?? '-'}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Identity Hash
                  </Text>
                  <Text fontSize="xs" fontFamily="mono">
                    {selectedResult.identityHash ?? '-'}
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="xs" variant="subtle">
                    Delivered
                  </Text>
                  <Tag
                    label={selectedResult.eventDelivered ? 'Yes' : 'No'}
                    variant={selectedResult.eventDelivered ? 'success' : 'default'}
                    size="sm"
                  />
                </Flex>
              </Flex>
              {messagePayload && (
                <CodeEditor
                  value={JSON.stringify(messagePayload, null, 2)}
                  onChange={() => {}}
                  language="json"
                  readOnly
                  minHeight="300px"
                />
              )}
            </Flex>
          )}
        </Modal>
      </Flex>
    </PageContainer>
  );
}
